export interface SaebReadingLevel {
  levelIndex: 1 | 2 | 3 | 4;
  title: string;
  emoji: string;
  getDescription: (score: number, questionCount?: number) => string;
}

// GA-05 confirmed by Mari on Slack (2026-09-03):
// 26 questions is the real number for the Reading Cycle (level_title present). This screen
// (`/atividades/simulado/:id/resultado`) is generic for any SAEB simulado, not just the Reading
// Cycle's — that's why `getDescription` takes `questionCount` from the outside instead of fixing
// a value here; `resolveSaebFeedbackLevel` decides the right number by `source` ('backend' → 26,
// 'computed' → 30, the legacy SAEB simulado's real value that this screen already showed before
// this feature).
export const DEFAULT_QUESTION_COUNT = 26;

export const SAEB_READING_LEVELS: SaebReadingLevel[] = [
  {
    levelIndex: 1,
    title: 'Leitor Iniciante',
    emoji: '🌱',
    getDescription: (score, questionCount = DEFAULT_QUESTION_COUNT) =>
      `Você concluiu o simulado de ${questionCount} perguntas! Com a nota ${score} de 500, este é o seu ponto de partida na leitura. Sua trilha vai te ajudar a avançar. Confira sua nota!`,
  },
  {
    levelIndex: 2,
    title: 'Leitor Aprendiz',
    emoji: '📖',
    getDescription: (score, questionCount = DEFAULT_QUESTION_COUNT) =>
      `Você concluiu o simulado de ${questionCount} perguntas! Com a nota ${score} de 500, você já domina várias habilidades de leitura. Sua trilha vai te levar ainda mais longe. Confira sua nota!`,
  },
  {
    levelIndex: 3,
    title: 'Leitor Em Prática',
    emoji: '💪',
    getDescription: (score, questionCount = DEFAULT_QUESTION_COUNT) =>
      `Você concluiu o simulado de ${questionCount} perguntas! Com a nota ${score} de 500, você lê com atenção e entende bastante coisa. Confira sua nota e siga evoluindo!`,
  },
  {
    levelIndex: 4,
    title: 'Leitor Confiante',
    emoji: '🏅',
    getDescription: (score, questionCount = DEFAULT_QUESTION_COUNT) =>
      `Você concluiu o simulado de ${questionCount} perguntas com maestria! Com a nota ${score} de 500, você chegou ao topo da escala de leitura. Confira sua nota e celebre essa conquista!`,
  },
];

export default function getSaebReadingLevel(score: number): SaebReadingLevel {
  if (score < 250) return SAEB_READING_LEVELS[0];
  if (score < 275) return SAEB_READING_LEVELS[1];
  if (score < 375) return SAEB_READING_LEVELS[2];
  return SAEB_READING_LEVELS[3];
}

// Slice 5 (SAEB Diagnostic by blocks, doc section 7): for the Reading Cycle the student-facing
// level comes from a SIMPLE COUNT OF CORRECT ANSWERS (0-26), not the weighted 0-500 score. Bands:
// N1 = 0-10, N2 = 11-16, N3 = 17-21, N4 = 22-26. [confidence: high] (doc section 7)
// This is ADDITIVE — the legacy SAEB simulado keeps the score-based `getSaebReadingLevel` above;
// only the Reading Cycle branch (source: 'backend') uses this one (see resolveSaebFeedbackLevel).
export function getSaebReadingLevelByCorrectCount(
  correctCount: number,
): SaebReadingLevel {
  if (correctCount <= 10) return SAEB_READING_LEVELS[0];
  if (correctCount <= 16) return SAEB_READING_LEVELS[1];
  if (correctCount <= 21) return SAEB_READING_LEVELS[2];
  return SAEB_READING_LEVELS[3];
}

// Slice 5: on the Reading Cycle the score (0-500) is removed from the student's screen, so the
// band description must NOT cite "com a nota X de 500" (the score-based `getDescription` above
// still does, for the legacy simulado). Copy here is a PLACEHOLDER — final wording is [GATE-PM]
// (see requirements.md, R3). Keeps the "x de N" framing the new screen shows.
//
// `mentionsUpcomingTrail` (default true): levels 1-2's copy ends with a "sua trilha vai te
// ajudar/levar..." teaser, which is only true on the diagnostic (SAEB 1) — by the time the
// student reaches the final SAEB (SAEB 2, comparison screen), the trilha already happened, so
// repeating that teaser there wrongly implies another activity is still coming (user finding,
// 2026-09-24). Callers pass `false` for the final/comparison activity.
export function getSaebReadingLevelDescriptionByCorrectCount(
  levelIndex: 1 | 2 | 3 | 4,
  correctCount: number,
  questionCount: number = DEFAULT_QUESTION_COUNT,
  mentionsUpcomingTrail: boolean = true,
): string {
  switch (levelIndex) {
    case 1:
      return `Você acertou ${correctCount} de ${questionCount} questões. Este é o seu ponto de partida na leitura.${
        mentionsUpcomingTrail ? ' Sua trilha vai te ajudar a avançar!' : ''
      }`;
    case 2:
      return `Você acertou ${correctCount} de ${questionCount} questões. Você já domina várias habilidades de leitura.${
        mentionsUpcomingTrail
          ? ' Sua trilha vai te levar ainda mais longe!'
          : ''
      }`;
    case 3:
      return `Você acertou ${correctCount} de ${questionCount} questões. Você lê com atenção e entende bastante coisa — siga evoluindo!`;
    case 4:
    default:
      return `Você acertou ${correctCount} de ${questionCount} questões com maestria! Você chegou ao topo da escala de leitura. Celebre essa conquista!`;
  }
}
